import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { createHiringApplication } from "../../../api/services/hroneApi";
import { useForm } from "react-hook-form";
import { BsCloudUpload } from "react-icons/bs";
import {
  StyledInput,
  StyledInputContainer,
  StyledInputGroup,
  StyledLabel,
  StyledSelect,
  StyledSelectContainer,
  StyledSelectGroup,
} from "../../../StyledCss/styledInputs";
import { PrimaryButton, SecondaryButton } from "../../../StyledCss/styledbtn";
import { ErrorText } from "../../../StyledCss/styledComponents";
import {
  EMAIL_PATTERN,
  MOBILE_PATTERN,
  isPdfResumeFile,
  validateTextField,
  validateNumericField,
} from "../../../util/hiringUtils";
import "../CreateJobModal/index.css";
import "./index.css";

const APPLICATION_SOURCES = [
  "LinkedIn",
  "Naukri",
  "Referral",
  "Indeed",
  "Company Website",
  "Other",
];

const RESUME_ACCEPT = "application/pdf,.pdf";
const RESUME_INPUT_ID = "add-candidate-resume";

const DEFAULT_VALUES = {
  firstName:         "",
  lastName:          "",
  email:             "",
  mobile:            "",
  currentLocation:   "",
  experienceType:    "EXPERIENCED",
  totalExperience:   "",
  currentCompany:    "",
  currentCTC:        "",
  expectedCTC:       "",
  noticePeriod:      "",
  applicationSource: "",
  otherSource:       "",
  status:            "APPLIED",
};


const AddCandidateModal = ({ jobId, onSuccess, onCancel }) => {
  const orgDetails   = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#e86020";

  const [resumeFile,   setResumeFile]   = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({ defaultValues: DEFAULT_VALUES });

  const applicationSource = watch("applicationSource");
  const experienceType    = watch("experienceType");
  const isFresher         = experienceType === "FRESHER";
  const isOtherSource     = applicationSource === "Other";

  useEffect(() => {
    reset(DEFAULT_VALUES);
    setResumeFile(null);
    const inp = document.getElementById(RESUME_INPUT_ID);
    if (inp) inp.value = "";
  }, [reset]);

  useEffect(() => {
    if (!isOtherSource) clearErrors("otherSource");
  }, [isOtherSource, clearErrors]);

  useEffect(() => {
    if (!isFresher) return;
    setValue("totalExperience", "");
    setValue("noticePeriod", "");
    setValue("currentCompany", "");
    setValue("currentCTC", "");
  }, [isFresher, setValue]);

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isPdfResumeFile(file)) {
      setError("resume", { type: "manual", message: "Only PDF files are allowed" });
      setResumeFile(null);
      e.target.value = "";
      return;
    }
    clearErrors("resume");
    setResumeFile(file);
  };

  const clearResume = () => {
    setResumeFile(null);
    clearErrors("resume");
    const inp = document.getElementById(RESUME_INPUT_ID);
    if (inp) inp.value = "";
  };

  const openResumePicker = () => {
    document.getElementById(RESUME_INPUT_ID)?.click();
  };

  const onSubmit = handleSubmit(async (data) => {
    if (data.applicationSource === "Other" && !data.otherSource?.trim()) {
      setError("otherSource", { type: "manual", message: "Please specify the application source" });
      return;
    }

    const { otherSource, applicationSource: source, experienceType: expType } = data;
    const isFresh = expType === "FRESHER";
    const resolvedSource = source === "Other" ? otherSource.trim() : source;

    const fd = new FormData();
    fd.append("first_name",         data.firstName.trim());
    fd.append("last_name",          data.lastName.trim());
    fd.append("email",              data.email.trim());
    fd.append("mobile",             data.mobile.trim());
    fd.append("experience_type",    expType);
    fd.append("status",             data.status);
    if (data.currentLocation)       fd.append("current_location",   data.currentLocation);
    if (resolvedSource)             fd.append("application_source", resolvedSource);
    if (!isFresh && data.totalExperience) fd.append("total_experience", data.totalExperience);
    if (!isFresh && data.currentCompany)  fd.append("current_company",  data.currentCompany);
    if (!isFresh && data.currentCTC)      fd.append("current_ctc",      data.currentCTC);
    if (!isFresh && data.noticePeriod)    fd.append("notice_period",    data.noticePeriod);
    if (data.expectedCTC)           fd.append("expected_ctc",      data.expectedCTC);
    if (resumeFile)                 fd.append("resume",            resumeFile, resumeFile.name);

    setIsSubmitting(true);
    try {
      const res  = await createHiringApplication(jobId, fd);
      const body = await res.json();
      if (!body.status) throw new Error(body.detail || body.message || "Failed to add candidate");
      onSuccess(body.data);
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  });

  const errBorder = (field) => (errors[field] ? { borderLeft: "10px solid red" } : {});

  const Inp = ({ name, label, type = "text", required = false, rules = {}, inputProps = {} }) => (
    <StyledInputGroup>
      <StyledInputContainer>
        <StyledInput
          type={type}
          placeholder=" "
          style={{ width: "100%", ...errBorder(name) }}
          {...inputProps}
          {...register(name, { required, ...rules })}
        />
        <StyledLabel className="user-label">{label}{required ? " *" : ""}</StyledLabel>
      </StyledInputContainer>
      {errors[name] && <ErrorText>{errors[name].message || `${label} is required`}</ErrorText>}
    </StyledInputGroup>
  );

  const MOBILE_RULE = { value: MOBILE_PATTERN, message: "Enter a valid mobile number" };
  const textRule = (label, required = false) => ({
    validate: (v) => validateTextField(v, label, { required }) || true,
  });
  const numericRule = (label, opts = {}) => ({
    validate: (v) => validateNumericField(v, label, opts) || true,
  });

  return (
    <form
      className="create-job-form"
      onSubmit={onSubmit}
      autoComplete="off"
      noValidate
      style={{ "--hiring-primary": primaryColor }}
    >
      <div className="create-job-form-body">

        <div className="create-job-section">
          <p className="create-job-section-title">Personal Information</p>
          <div className="create-job-grid-2col">
            <Inp name="firstName" label="First Name" required rules={textRule("First name", true)} />
            <Inp name="lastName"  label="Last Name"  required rules={textRule("Last name", true)} />
            <Inp
              name="email" label="Email" type="email" required
              rules={{ pattern: { value: EMAIL_PATTERN, message: "Enter a valid email address" } }}
            />
            <Inp name="mobile"    label="Mobile Number" required rules={{ pattern: MOBILE_RULE }} />
            <div className="create-job-field-full">
              <Inp name="currentLocation" label="Current Location" rules={textRule("Current location", false)} />
            </div>
          </div>
        </div>

        <div className="create-job-section">
          <p className="create-job-section-title">Professional Information</p>
          <div className="create-job-grid-2col">
            <StyledSelectGroup>
              <StyledSelectContainer>
                <StyledSelect width="100%" {...register("experienceType")}>
                  <option value="EXPERIENCED">Experienced</option>
                  <option value="FRESHER">Fresher</option>
                </StyledSelect>
                <StyledLabel hasValue className="user-label">Candidate Type</StyledLabel>
              </StyledSelectContainer>
            </StyledSelectGroup>

            {!isFresher && (
              <>
                <Inp
                  name="totalExperience" label="Total Experience (years)" type="number"
                  rules={numericRule("Experience")} inputProps={{ min: 0, step: "0.1" }}
                />
                <Inp name="currentCompany"  label="Current Company" rules={textRule("Current company", false)} />
                <Inp
                  name="currentCTC" label="Current CTC" type="number"
                  rules={numericRule("Current CTC")} inputProps={{ min: 0, step: "0.01" }}
                />
                <Inp
                  name="noticePeriod" label="Notice Period (days)" type="number"
                  rules={numericRule("Notice period", { integer: true })} inputProps={{ min: 0, step: "1" }}
                />
              </>
            )}

            <Inp
              name="expectedCTC" label="Expected CTC" type="number"
              rules={numericRule("Expected CTC")} inputProps={{ min: 0, step: "0.01" }}
            />
          </div>
        </div>

        <div className="create-job-section">
          <p className="create-job-section-title">Application Details</p>
          <div className="create-job-grid-2col">
            <StyledSelectGroup>
              <StyledSelectContainer>
                <StyledSelect width="100%" {...register("applicationSource")}>
                  <option value="">— Select Source —</option>
                  {APPLICATION_SOURCES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </StyledSelect>
                <StyledLabel hasValue className="user-label">Application Source</StyledLabel>
              </StyledSelectContainer>
            </StyledSelectGroup>

            {isOtherSource && (
              <StyledInputGroup>
                <StyledInputContainer>
                  <StyledInput
                    type="text"
                    placeholder=" "
                    style={{ width: "100%", ...errBorder("otherSource") }}
                    {...register("otherSource", {
                      required: isOtherSource,
                      validate: (v) => validateTextField(v, "Application source", { required: isOtherSource }) || true,
                    })}
                  />
                  <StyledLabel className="user-label">Specify Source *</StyledLabel>
                </StyledInputContainer>
                {errors.otherSource && (
                  <ErrorText>{errors.otherSource.message || "Please specify the application source"}</ErrorText>
                )}
              </StyledInputGroup>
            )}

            <StyledSelectGroup>
              <StyledSelectContainer>
                <StyledSelect width="100%" {...register("status")}>
                  <option value="APPLIED">Applied</option>
                  <option value="SHORTLISTED">Shortlisted</option>
                  <option value="HIRED">Hired</option>
                  <option value="REJECTED">Rejected</option>
                </StyledSelect>
                <StyledLabel hasValue className="user-label">Status</StyledLabel>
              </StyledSelectContainer>
            </StyledSelectGroup>
          </div>
        </div>

        <div className="create-job-section create-job-section-last">
          <p className="create-job-section-title">Resume</p>
          <div className="create-job-grid-2col">
            <div className="add-candidate-resume-field">
              <div
                className="add-candidate-file-picker"
                onClick={openResumePicker}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && openResumePicker()}
              >
                <input
                  id={RESUME_INPUT_ID}
                  type="file"
                  accept={RESUME_ACCEPT}
                  style={{ display: "none" }}
                  onChange={handleResumeChange}
                />
                <BsCloudUpload
                  style={{
                    fontSize: "15px",
                    color: resumeFile ? primaryColor : "#9ca3af",
                    flexShrink: 0,
                  }}
                />
                <span className={resumeFile ? "add-candidate-file-name" : "add-candidate-file-placeholder"}>
                  {resumeFile ? resumeFile.name : "Click to select resume (PDF only)"}
                </span>
                {resumeFile && (
                  <button
                    type="button"
                    className="add-candidate-file-clear-btn"
                    title="Remove file"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearResume();
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
              {errors.resume && <ErrorText>{errors.resume.message}</ErrorText>}
            </div>
          </div>
        </div>

        <div className="create-job-actions">
          <SecondaryButton type="button" onClick={onCancel} disabled={isSubmitting}>Cancel</SecondaryButton>
          <PrimaryButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Adding…" : "Add Candidate"}
          </PrimaryButton>
        </div>

      </div>
    </form>
  );
};

export default AddCandidateModal;
